// Контекст drag-and-drop: единственный источник состояния «что тащим» / «куда наведено».
//
// Ключевое решение: библиотека (src/ui/library) начинает HTML5-драг сама (dataTransfer +
// data-segment-id на карточке), не зная о нашем контексте. Чтобы не требовать координации
// между зонами, DndProvider слушает нативные dragstart/dragend на document (capture-фаза) и
// САМ восстанавливает DragPayload по DOM-атрибутам перетаскиваемого элемента:
//  - data-segment-id  -> segmentId
//  - ближайший предок с data-drop-variant -> fromVariantId (null, если карточка из библиотеки)
//  - ближайший предок с data-drop-index   -> fromIndex (null, если fromVariantId нет)
// Это работает для любого источника драга, если он использует общий SEGMENT_DND_MIME и
// расставляет эти атрибуты — ровно то, что уже делает src/ui/library/SegmentCard.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DROP_INDEX_ATTR, DROP_VARIANT_ATTR, SEGMENT_DND_MIME, SEGMENT_ID_ATTR } from './constants';

export type DragPayload = {
  segmentId: string;
  fromVariantId: string | null;
  fromIndex: number | null;
};

export interface DndApi {
  drag: DragPayload | null;
  /** индикатор места вставки: null или {variantId, index} */
  over: { variantId: string; index: number } | null;
  begin(p: DragPayload): void;
  setOver(o: { variantId: string; index: number } | null): void;
  end(): void;
  /** true пока идёт тач-перетаскивание (для подавления скролла/hover) */
  touchActive: boolean;
}

/** Расширение для внутреннего использования useTouchDrag — не часть публичного контракта DndApi. */
interface InternalDndApi extends DndApi {
  setTouchActive(active: boolean): void;
}

const DndCtx = createContext<InternalDndApi | null>(null);

function payloadFromElement(el: Element): DragPayload | null {
  const source = el.closest<HTMLElement>(`[${SEGMENT_ID_ATTR}]`);
  const segmentId = source?.getAttribute(SEGMENT_ID_ATTR);
  if (!source || !segmentId) return null;

  const variantEl = source.closest<HTMLElement>(`[${DROP_VARIANT_ATTR}]`);
  const fromVariantId = variantEl?.getAttribute(DROP_VARIANT_ATTR) ?? null;

  let fromIndex: number | null = null;
  if (fromVariantId) {
    const indexEl = source.closest<HTMLElement>(`[${DROP_INDEX_ATTR}]`);
    const raw = indexEl?.getAttribute(DROP_INDEX_ATTR);
    const parsed = raw !== null && raw !== undefined ? Number(raw) : NaN;
    fromIndex = Number.isNaN(parsed) ? null : parsed;
  }

  return { segmentId, fromVariantId, fromIndex };
}

export function DndProvider({ children }: { children: ReactNode }) {
  const [drag, setDrag] = useState<DragPayload | null>(null);
  const [over, setOverState] = useState<{ variantId: string; index: number } | null>(null);
  const [touchActive, setTouchActiveState] = useState(false);
  const dragRef = useRef<DragPayload | null>(null);

  const begin = useCallback((p: DragPayload) => {
    dragRef.current = p;
    setDrag(p);
  }, []);

  const setOver = useCallback((o: { variantId: string; index: number } | null) => {
    setOverState(o);
  }, []);

  const end = useCallback(() => {
    dragRef.current = null;
    setDrag(null);
    setOverState(null);
    setTouchActiveState(false);
  }, []);

  const setTouchActive = useCallback((active: boolean) => setTouchActiveState(active), []);

  // Автоматическое восстановление payload из DOM для любого источника нативного HTML5 DnD
  // (библиотека, таймлайн — не важно кто начал драг).
  useEffect(() => {
    function onDragStart(e: DragEvent) {
      if (!(e.target instanceof Element)) return;
      const types = e.dataTransfer?.types;
      if (types && types.length > 0 && !Array.from(types).includes(SEGMENT_DND_MIME)) {
        // не наш драг (например, выделение текста) — не мешаем
        return;
      }
      const payload = payloadFromElement(e.target);
      if (payload) {
        dragRef.current = payload;
        setDrag(payload);
      }
    }
    function onDragEnd() {
      if (dragRef.current !== null) {
        dragRef.current = null;
        setDrag(null);
        setOverState(null);
      }
    }
    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('dragend', onDragEnd, true);
    return () => {
      document.removeEventListener('dragstart', onDragStart, true);
      document.removeEventListener('dragend', onDragEnd, true);
    };
  }, []);

  const api = useMemo<InternalDndApi>(
    () => ({ drag, over, begin, setOver, end, touchActive, setTouchActive }),
    [drag, over, begin, setOver, end, touchActive, setTouchActive],
  );

  return <DndCtx.Provider value={api}>{children}</DndCtx.Provider>;
}

export function useDnd(): DndApi {
  const ctx = useContext(DndCtx);
  if (!ctx) throw new Error('useDnd должен вызываться внутри <DndProvider>');
  return ctx;
}

/** Только для useTouchDrag.ts — доступ к setTouchActive, не входит в публичный DndApi. */
export function useDndInternal(): InternalDndApi {
  const ctx = useContext(DndCtx);
  if (!ctx) throw new Error('useDndInternal должен вызываться внутри <DndProvider>');
  return ctx;
}
